import { Component, computed, input } from '@angular/core'
import { Icon, IconComponent } from '../icon'
import { TooltipModule, TooltipPosition } from '../tooltip'

export enum BadgeColors {
  MediumGrey = 'medium-grey',
  LightGrey = 'light-grey',
  Primary = 'primary',
  DarkPrimary = 'dark-primary',
  Blueberry = 'blueberry',
  Apple = 'apple',
  Error = 'error',
  Mint = 'mint',
  Lemon = 'lemon',
  Mandarin = 'mandarin',
  Watermelon = 'watermelon',
  Brand = 'brand',
  Grape = 'grape',
  Secondary = 'secondary',
}
export type BadgeColor = `${BadgeColors}`

export const BadgeDisplayTypes = {
  Default: 'default',
  Small: 'small',
  Tile: 'tile',
  Inline: 'inline',
} as const
export type BadgeDisplayType = (typeof BadgeDisplayTypes)[keyof typeof BadgeDisplayTypes]

export interface BadgeConfig {
  color: BadgeColor
  icon: Icon
  text: string
  ariaLabel?: string
}

/**
 * Component for displaying a concise status indicator or label. Badge content can be just text,
 * just an icon, or both.
 *
 * @example
 * ``` html
 * <ftr-badge text="New" icon="RecordLarge" color="primary" />
 * ```
 */
@Component({
  selector: 'ftr-badge',
  templateUrl: './badge.component.html',
  styleUrl: './badge.component.css',
  imports: [IconComponent, TooltipModule],
})
export class BadgeComponent {
  /** @see {@link BadgeColors} */
  readonly color = input<BadgeColor | BadgeColors>(BadgeColors.MediumGrey)
  readonly text = input<string>()
  readonly icon = input<Icon>()

  /** @see {@link BadgeDisplayTypes} */
  readonly displayType = input<BadgeDisplayType>(BadgeDisplayTypes.Default)

  /** Explicitly set width when text should truncate, otherwise badge width spans content. */
  readonly maxWidth = input<string>()

  readonly tooltipLabel = input<string>()
  readonly tooltipPosition = input<TooltipPosition>('below')
  readonly ariaLabel = input<string>()
  readonly iconAriaHidden = input<boolean>()
  readonly ariaHidden = input<boolean>()
  readonly ariaRole = input('status')

  protected readonly getTooltipLabel = computed<string>(() => {
    return this.tooltipLabel() ?? (this.maxWidth() && this.text()) ?? ''
  })

  protected readonly getAriaLabel = computed<string | undefined>(() => {
    return this.ariaHidden() ? undefined : this.ariaLabel()
  })

  protected readonly getClasses = computed<Record<string, boolean>>(() => ({
    badge: true,
    [`badge--color-${this.color()}`]: true,
    'badge--inline': this.displayType() === 'inline',
    'badge--small': this.displayType() === 'small',
    'badge--tile': this.displayType() === 'tile',
    'badge--truncated-width': !!this.maxWidth(),
  }))
}
